import segmentPlugin from '@analytics/segment'
import Analytics from 'analytics'

import {
  AnalyticsAction,
  AnalyticsActionType,
  AnalyticsPageAction,
  AnalyticsTrackAction,
} from 'types/common/analytics.type'
import { excludeFalsy, isString } from 'utils/common'

export const analytics = Analytics({
  app: 'tccc-experience-loop',
  plugins: [
    segmentPlugin({
      writeKey: process.env.REACT_APP_SEGMENT_ANALYTICS_KEY,
    }),
  ].filter(excludeFalsy),
})

export const reset = () => {
  analytics.reset()
}

const getAnalyticsTenantAzId = () => analytics?.getState()?.user?.traits?.tenantAzId

const trackPage = ({ payload }: AnalyticsPageAction) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    analytics.page(
      {
        title: payload,
      },
      {
        agency: analyticsTenantAzId,
      },
    )
  }
}

const trackAction = ({ payload }: AnalyticsTrackAction) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    if (isString(payload)) {
      analytics.track(payload, {
        agency: analyticsTenantAzId,
      })
    } else {
      const { action, params } = payload

      analytics.track(action, {
        agency: analyticsTenantAzId,
        ...Object.fromEntries(params.map(({ key, value }) => [key, value])),
      })
    }
  }
}

export const trackAnalytics = (action: AnalyticsAction) => {
  switch (action.type) {
    case AnalyticsActionType.page:
      trackPage(action)
      break

    case AnalyticsActionType.action:
      trackAction(action)
      break

    default:
      break
  }
}

export const EVENTS = {
  PAGE: { EXPLOOP: 'pg_pv_experienceloop' },

  ACTION: {
    EXPLOOP_HEADER_VIEWVIDEO: 'pg_act_experienceloop_header_viewvideo',
    EXPLOOP_EXPLOOP_CFP_IMXDIGITAL_LEARNMORE: 'pg_act_experienceloop_cfp_imxdigital_learnmore',

    EXPLOOP_LOOPPROCESS_VIEW: (title: string) => `pg_act_experienceloop_loopprocess_view${title}`,
    EXPLOOP_CFP: (state: 'expandcard' | 'minimizecard') => `pg_act_experienceloop_cfp_${state}`,
    EXPLOOP_EXPLOOP_PROOFLOOP_VIEWCAROUSEL: (index: number) => `pg_act_experienceloop_proofloop_viewcarrousel${index}`,
    EXPLOOP_EXPLOOP_CAROUSEL: (state: 'left' | 'right') => `pg_act_experienceloop_carousel${state}`,
    EXPLOOP_EXPLOOP_LOOPPROCESS_VIEW: (state: 'expand' | 'minimize', index: number, title: string) =>
      `pg_act_experienceloop_loopprocess_view${title}_${state}${index}`,

    LANDING: {
      VIDEO: 'pg_act_experienceloop_landing_header_viewvideo',
      EXPLORE_MORE: 'pg_act_experienceloop_landing_exploremore',
      DOWNLOAD_FULL_DECK: 'pg_act_experienceloop_landing_downloaddeck',
      NMA_1: 'pg_act_experienceloop_landing_nma_firstitem',
      NMA_2: 'pg_act_experienceloop_landing_nma_seconditem',
      NMA_3: 'pg_act_experienceloop_landing_nma_thirditem',
    },
    BACK_TO_LANDING_TOP: 'pg_act_experienceloop_backtolanding_top',
    BACK_TO_LANDING_BOTTOM: 'pg_act_experienceloop_backtolanding_bottom',
    ASSEMBLE: {
      TO_EXPLORE: 'pg_act_experienceloop_assemble_goto_explore',
      TABS: {
        TAB_1_CTA: 'pg_act_experienceloop_assemble_firstitem_openmural',
        TAB_2_CTA: 'pg_act_experienceloop_assemble_seconditem_openmural',
        TAB_2_VIDEO: 'pg_act_experienceloop_assemble_seconditem_viewvideo',
        TAB_4_CTA: 'pg_act_experienceloop_assemble_forthitem_openmural',
        TAB_5_CTA: 'pg_act_experienceloop_assemble_fifthitem_openmural',
        TAB_5_VIDEO: 'pg_act_experienceloop_assemble_fifthitem_viewvideo',
        TAB_6_VIDEO: 'pg_act_experienceloop_assemble_sixthitem_viewvideo',
      },
    },
    EXPLORE: {
      TO_ASSEMBLE: 'pg_act_experienceloop_explore_gobackto_assemble',
      TO_CREATE: 'pg_act_experienceloop_explore_goto_create',
    },
    CREATE: {
      TO_EXPLORE: 'pg_act_experienceloop_create_backto_explore',
      TO_SHAPE: 'pg_act_experienceloop_create_goto_shape',
    },
    SHAPE: {
      TO_CREATE: 'pg_act_experienceloop_shape_backto_create',
      TO_PRODUCE: 'pg_act_experienceloop_shape_goto_produce',
    },
    PRODUCE: {
      TO_SHAPE: 'pg_act_experienceloop_produce_backto_shape',
      TO_LEARN: 'pg_act_experienceloop_produce_goto_learn',
    },
    LEARN: {
      TO_PRODUCE: 'pg_act_experienceloop_learn_backto_produce',
    },
    FRAMEWORK: (page: string) => `pg_act_experienceloop_framework_${page}`,
  },
}
